import { ModelGardenApi } from '../../api/modelgarden.api';

import { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import { ModelData, ModelNotFoundError, LeaderboardEntry } from '../../api';
import {
  NexusLeaderboardEntry,
  NexusModelResponse,
} from '../../api/modelgarden.objects';

/**
 * Service for retrieving AI model information
 */
export class ModelGardenService implements ModelGardenApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;
  /** Maximum number of icon entries kept in the LRU cache before the oldest is evicted. */
  private readonly iconCacheMaxSize = 50;
  /** LRU cache keyed by iconId — Map insertion order acts as recency; the first entry is the least-recently used. */
  private readonly iconCache = new Map<string, Promise<Blob | null>>();

  constructor(discoveryApi: DiscoveryApi, fetchApi: FetchApi) {
    this.discoveryApi = discoveryApi;
    this.fetchApi = fetchApi;
  }

  private async getGatewayBase(): Promise<string> {
    return `${await this.discoveryApi.getBaseUrl(
      'auth-gateway',
    )}/aiecosystem-nexus`;
  }

  /**
   * Get all models available in the system via the Nexus API.
   *
   * @returns An array of all model data
   */
  async getAllModels(): Promise<ModelData[]> {
    const proxyUrl = await this.getGatewayBase();

    const response = await this.fetchApi.fetch(
      `${proxyUrl}/mis/v1/model-garden`,
      {
        method: 'GET',
        headers: { Accept: 'application/json' },
      },
    );

    if (!response.ok) {
      throw new Error(
        `Error - Statuscode ${response.status} - ${response.statusText} - Failed to fetch models.`,
      );
    }

    const data = await response.json();
    const rawModels: NexusModelResponse[] = Array.isArray(data) ? data : [];

    return rawModels.map(raw => this.mapToModelData(raw));
  }

  /**
   * Get model by ID via the Nexus API
   *
   * @param id The unique identifier of the model
   * @throws {ModelNotFoundError} When the model with the given ID does not exist
   * @returns The model data
   */
  async getModelById(id: string): Promise<ModelData> {
    const proxyUrl = await this.getGatewayBase();
    const url = `${proxyUrl}/mis/v1/models/${encodeURIComponent(id)}`;

    const response = await this.fetchApi.fetch(url, {
      method: 'GET',
      headers: { Accept: 'application/json' },
    });

    if (response.status === 404) {
      throw new ModelNotFoundError(id);
    }

    if (!response.ok) {
      throw new Error(
        `Error - Statuscode ${response.status} - ${response.statusText} - Failed to fetch model.`,
      );
    }

    const raw: NexusModelResponse = await response.json();
    return this.mapToModelData(raw);
  }
  /**
   * Fetches the icon file for a given icon ID, using a size-capped LRU cache to avoid
   * redundant network requests and unbounded memory growth across long sessions.
   * @param iconId The iconID of the icon to fetch from Nexus API.
   * @returns A promise that resolves to the icon file as a Blob, or null if the icon is not a supported image type.
   */
  async getIconFile(iconId: string): Promise<Blob | null> {
    const cached = this.iconCache.get(iconId);
    if (cached) {
      // Promote to most-recently-used by re-inserting at the end
      this.iconCache.delete(iconId);
      this.iconCache.set(iconId, cached);
      return cached;
    }

    // Evict the least-recently-used entry when the cache is full
    if (this.iconCache.size >= this.iconCacheMaxSize) {
      const oldestKey = this.iconCache.keys().next().value;
      if (oldestKey !== undefined) this.iconCache.delete(oldestKey);
    }

    const fetchPromise = this.fetchIconFile(iconId);
    this.iconCache.set(iconId, fetchPromise);
    return fetchPromise;
  }

  /**
   * Clears the in-memory icon cache. Call this when leaving the Model Garden
   * to free Blob memory accumulated during the session.
   */
  clearIconCache(): void {
    this.iconCache.clear();
  }

  /**
   * Fetches model leaderboard data from the Nexus /mis/v1/leaderboard endpoint.
   * @returns An array of leaderboard entries with metric scores
   */
  async getModelLeaderboardData(): Promise<LeaderboardEntry[]> {
    const proxyUrl = await this.getGatewayBase();

    const response = await this.fetchApi.fetch(
      `${proxyUrl}/mis/v1/leaderboard`,
      {
        method: 'GET',
        headers: { Accept: 'application/json' },
      },
    );

    if (!response.ok) {
      throw new Error(
        `Error - Statuscode ${response.status} - ${response.statusText} - Failed to fetch leaderboard data.`,
      );
    }

    const data = await response.json();
    const rawEntries: NexusLeaderboardEntry[] = Array.isArray(data) ? data : [];

    return rawEntries.map(raw => this.mapToLeaderboardEntry(raw));
  }

  /**
   * Fetches the icon file for a given icon ID directly from the Nexus API, without using the cache. This is used internally by getIconFile which handles caching.
   * @param iconId The iconID of the icon to fetch from Nexus API.
   * @returns A promise that resolves to the icon file as a Blob, or null if the icon is not a supported image type.
   */
  private async fetchIconFile(iconId: string): Promise<Blob | null> {
    const proxyUrl = await this.getGatewayBase();
    const iconUrl = `${proxyUrl}/mis/v1/icons/${encodeURIComponent(iconId)}`;

    const response = await this.fetchApi.fetch(`${iconUrl}`, {
      method: 'GET',
    });

    if (!response.ok) {
      if (response.status === 404) {
        return null;
      }
      throw new Error(
        `Error - Statuscode ${response.status} - ${response.statusText} - Failed to fetch icon with ID ${iconId}.`,
      );
    }

    const blob = await response.blob();
    return blob.type.includes('webp') ||
      blob.type.includes('png') ||
      blob.type.includes('jpeg') ||
      blob.type.includes('jpg')
      ? blob
      : null;
  }

  /**
   * Maps a raw Nexus model response to the ModelData structure used in the frontend
   * @param raw The raw model data from the Nexus API
   * @returns The mapped ModelData object
   */
  private mapToModelData(raw: NexusModelResponse): ModelData {
    return {
      id: raw.id,
      name: raw.name,
      longDescription: raw.longDescription,
      licenseType: raw.licenseType,
      selfHostable: raw.selfHostable,
      iconId: raw.iconId,
      creator: raw.creator,
      releaseDate: raw.releaseDate,
      dataTypesInput: raw.dataTypesInput,
      dataTypesOutput: raw.dataTypesOutput,
      informationLastUpdated: raw.informationLastUpdated,
      deployments: raw.deployments,
      referenceLinks: raw.referenceLinks,
      annotations: raw.annotations,
    };
  }

  /**
   * Maps a raw Nexus leaderboard entry to the LeaderboardEntry structure used in
   * the frontend. The nested pricing object is flattened into pricePerMillion*
   * fields; missing pricing maps to null.
   * @param raw The raw leaderboard entry from the Nexus API
   * @returns The mapped LeaderboardEntry object
   */
  private mapToLeaderboardEntry(raw: NexusLeaderboardEntry): LeaderboardEntry {
    return {
      modelId: raw.modelId,
      intelligence: raw.intelligence,
      agentic: raw.agentic,
      coding: raw.coding,
      speed: raw.speed,
      pricePerMillionInputTokens: raw.pricing?.input ?? null,
      pricePerMillionOutputTokens: raw.pricing?.output ?? null,
    };
  }
}
